import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../lib/api";
import {
  createPushSubscription,
  getExistingPushSubscription,
  getPushSupport,
  removePushSubscription,
  type PushSupport
} from "../lib/notifications";
import {
  hasTurnAlertsEnabled,
  listTurnAlertGames,
  setTurnAlertsEnabled
} from "../lib/storage";
import { Button } from "./ui";

type TurnAlertState =
  | "checking"
  | "off"
  | "on"
  | "busy"
  | "error"
  | Exclude<PushSupport, "supported">;

export interface TurnAlertsController {
  state: TurnAlertState;
  message: string | null;
  toggle: () => Promise<void>;
}

function unsupportedState(support: PushSupport): TurnAlertState | null {
  return support === "supported" ? null : support;
}

export function useTurnAlerts(code: string, token: string | null): TurnAlertsController {
  const [state, setState] = useState<TurnAlertState>("checking");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const support = getPushSupport();
    const unavailable = unsupportedState(support);
    if (unavailable || !token) {
      setState(unavailable ?? "off");
      return () => {
        active = false;
      };
    }

    if (!hasTurnAlertsEnabled(code)) {
      setState("off");
      return () => {
        active = false;
      };
    }

    void getExistingPushSubscription()
      .then(async (subscription) => {
        if (!active) return;
        if (!subscription) {
          setTurnAlertsEnabled(code, false);
          setState("off");
          return;
        }
        await api.registerNotification(code, token, subscription.toJSON());
        if (active) setState("on");
      })
      .catch((caught) => {
        if (!active) return;
        setState("error");
        setMessage(
          caught instanceof ApiError
            ? caught.message
            : "Turn alerts could not be restored on this device."
        );
      });

    return () => {
      active = false;
    };
  }, [code, token]);

  const toggle = useCallback(async () => {
    if (!token || state === "busy" || state === "checking") return;
    const support = getPushSupport();
    const unavailable = unsupportedState(support);
    if (unavailable) {
      setState(unavailable);
      return;
    }

    setState("busy");
    setMessage(null);
    try {
      const disabling = hasTurnAlertsEnabled(code);
      // Request permission before any network or service-worker await so iOS keeps
      // the prompt tied to this exact button press.
      if (!disabling && Notification.permission === "default") {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          throw new Error("Notification permission was not granted.");
        }
      }
      const existing = await getExistingPushSubscription();
      if (disabling) {
        if (existing) await api.deleteNotification(code, token, existing.endpoint);
        setTurnAlertsEnabled(code, false);
        if (existing && listTurnAlertGames().length === 0) {
          await removePushSubscription(existing);
        }
        setState("off");
        setMessage("Turn alerts are off for this game.");
        return;
      }

      const config = await api.getNotificationConfig(code, token);
      const subscription = existing ?? (await createPushSubscription(config.publicKey));
      try {
        await api.registerNotification(code, token, subscription.toJSON());
      } catch (caught) {
        if (!existing && listTurnAlertGames().length === 0) {
          await removePushSubscription(subscription).catch(() => undefined);
        }
        throw caught;
      }
      setTurnAlertsEnabled(code, true);
      setState("on");
      setMessage("Turn alerts are on for this game.");
    } catch (caught) {
      const latestSupport = getPushSupport();
      const nowUnavailable = unsupportedState(latestSupport);
      setState(nowUnavailable ?? "error");
      setMessage(
        nowUnavailable === "denied"
          ? "Notifications are blocked. Allow them in this app’s browser or device settings."
          : caught instanceof ApiError
            ? caught.message
            : caught instanceof Error
              ? caught.message
              : "Turn alerts could not be changed."
      );
    }
  }, [code, state, token]);

  return { state, message, toggle };
}

function description(state: TurnAlertState): string {
  if (state === "on") return "This device will alert you when your rival plays.";
  if (state === "ios-install-required")
    return "On iPhone or iPad, first use Share → Add to Home Screen, then open the installed app.";
  if (state === "denied")
    return "Notifications are blocked. Allow them in your browser or device settings.";
  if (state === "unsupported") return "Turn alerts are not supported by this browser.";
  if (state === "error") return "Turn alerts need attention. Try again.";
  return "Get an alert on this device when your rival plays.";
}

export function TurnAlertsControl({
  controller,
  compact = false
}: {
  controller: TurnAlertsController;
  compact?: boolean;
}) {
  const enabled = controller.state === "on";
  const actionable = !["unsupported", "ios-install-required", "denied"].includes(
    controller.state
  );
  return (
    <div className={`turn-alerts-control${compact ? " is-compact" : ""}`}>
      <div>
        <strong>Turn alerts</strong>
        <small>{description(controller.state)}</small>
        {controller.message && <span role="status">{controller.message}</span>}
      </div>
      {actionable && (
        <Button
          variant={enabled ? "quiet" : "primary"}
          onClick={() => void controller.toggle()}
          disabled={controller.state === "checking" || controller.state === "busy"}
          aria-pressed={enabled}
          data-testid="turn-alerts-toggle"
        >
          {controller.state === "checking"
            ? "Checking…"
            : controller.state === "busy"
              ? "Working…"
              : enabled
                ? "Turn off"
                : "Enable alerts"}
        </Button>
      )}
    </div>
  );
}
